/**
 * The picture of an agent: a preview, a file picker and "Remove". Controlled: it shows `value`, a
 * data URI or nothing, and reports a new picture with an `avatar-change` event carrying the data
 * URI (undefined when removed); the page owns the state. The picture is made by avatarFromFile.
 */
import { LitElement, html } from 'lit';

import { avatarFromFile, initialOf } from '../avatar.js';
import { formStyles } from '../../styles/shared/forms.js';
import { planeAvatarInputStyles } from '../../styles/components/plane-avatar-input.js';

class PlaneAvatarInput extends LitElement {
  static properties = {
    value: { type: String },
    label: { type: String },
    maxLength: { type: Number },
    disabled: { type: Boolean },
    problem: { state: true },
    reading: { state: true },
  };

  static styles = [
    formStyles,
    planeAvatarInputStyles,
  ];

  constructor() {
    super();
    this.maxLength = 131072;
    this.reading = false;
    this.problem = null;
  }

  async choose(event) {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) {
      return;
    }
    this.reading = true;
    this.problem = null;
    try {
      this.report(await avatarFromFile(file, this.maxLength));
    } catch (error) {
      this.problem = error.message;
    } finally {
      this.reading = false;
    }
  }

  report(value) {
    this.dispatchEvent(new CustomEvent('avatar-change', { detail: { value }, bubbles: true, composed: true }));
  }

  render() {
    const initial = initialOf(this.label);
    return html`
      ${this.value ? html`<img class="preview" src=${this.value} alt="Avatar" />` : html`<span class="preview" aria-hidden="true">${initial}</span>`}
      <div class="controls">
        <div class="buttons">
          <button type="button" class="small" ?disabled=${this.disabled || this.reading} @click=${() => this.renderRoot.querySelector('input[type=file]').click()}>
            ${this.reading ? 'Reading…' : this.value ? 'Change picture' : 'Choose picture'}
          </button>
          ${this.value ? html`<button type="button" class="small danger" ?disabled=${this.disabled || this.reading} @click=${() => this.report(undefined)}>Remove</button>` : null}
        </div>
        <input type="file" accept="image/png,image/jpeg,image/webp,image/gif" aria-label="Avatar picture" @change=${(event) => this.choose(event)} />
        ${this.problem ? html`<span class="problem" role="alert">${this.problem}</span>` : null}
      </div>
    `;
  }
}

customElements.define('plane-avatar-input', PlaneAvatarInput);
